import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { 
    Button, 
    IconButton, 
    Paper, 
    Typography, 
    Input, 
    FormControl,
    FormControlLabel,
    RadioGroup,
    Radio
} from '@material-ui/core';
import * as tagSelectors from '../../selectors/tagSelectors';
import * as tagsConstants from '../../constants/tags';
import { BATCH_ACTION_TYPES } from '../../enums/AssetBatchActionTypes';
import { isValidTag } from '../../helpers/Util';
import AssetTagsList from './AssetTagsList';

const TAG_MODES = {
    ADD: 'add',
    REMOVE: 'remove'
};

class BatchTagModal extends React.Component {
    constructor(props) {
        super(props);
        
        this.state = {
            tagValue: '',
            selectedTags: [],
            mode: TAG_MODES.ADD
        };
    }

    onCloseModal = () => {
        this.setState({
            tagValue: '',
            selectedTags: [],
            mode: TAG_MODES.ADD
        });
        this.props.onClose();
    };

    onChangeInput = (event) => {
        const value = event.target.value.toLowerCase();
        this.setState({
            tagValue: value,
        });
    };

    onTagSelected = (tagValue, idx, isAlreadySelected) => {
        const { selectedTags } = this.state;
        const newSelectedTags = isAlreadySelected ? 
            selectedTags.filter(tag => tag !== tagValue) : 
            [...selectedTags, tagValue];
        
        this.setState({
            selectedTags: newSelectedTags
        });
    };

    onAddTag = (tagValue) => {
        const { selectedTags } = this.state;
        const { addTag } = this.props;
        
        if (!selectedTags.includes(tagValue)) {
            this.setState({
                selectedTags: [...selectedTags, tagValue],
                tagValue: ''
            });
        }
        
        addTag(tagValue);
    };

    onChangeMode = (event) => {
        this.setState({ 
            mode: event.target.value,
            selectedTags: []
        });
    };

    onApplyTags = () => {
        const { selectedAssets, executeBatchAction } = this.props;
        const { selectedTags, mode } = this.state;
        
        if (!selectedTags.length) {
            return;
        }

        this.setState({ 
            selectedTags: []
        });
        
        executeBatchAction(BATCH_ACTION_TYPES.TAG, selectedAssets, { 
            tags: selectedTags,
            mode: mode
        });
    };

    render() {
        const { show, classes, tags, selectedAssets } = this.props;
        const { tagValue, selectedTags, mode } = this.state;

        if (!show) {
            return null;
        }

        const selectedItemsCount = selectedAssets.length;
        const isAddMode = mode === TAG_MODES.ADD;

        // search for tag with space instead of empty spaces since tags cannot include empty spaces
        const tagWithoutSpaces = tagValue && tagValue.replace(/\s+/g, '_');
        const isTagValid = !tagValue || isValidTag(tagValue);
        const isTagValueNew = tagWithoutSpaces && tags.indexOf(tagWithoutSpaces) === -1;
        const addTag = isAddMode && isTagValueNew && isTagValid ? (
            <div className={classes.addNewTag} onClick={() => this.onAddTag(tagWithoutSpaces)}>
                <i className={classes.addNewTagIcon + " material-icons"}>add</i>
                <div className={classes.createTagButton}>Create "{tagValue}"</div>
            </div>
        ) : null;

        // Generate appropriate action button text based on mode
        const actionButtonText = isAddMode ? 'Add Tags' : 'Remove Tags';
        const headerText = isAddMode ? 'Add Tags' : 'Remove Tags';
        const descriptionText = isAddMode 
            ? 'Select tags to apply to all selected assets' 
            : 'Select tags to remove from all selected assets';

        return (
            <div className={classes.container}>
                <Paper className={classes.modal}>
                    <div className={classes.header}>
                        <Typography className={classes.title} variant="title">
                            {headerText} ({selectedItemsCount} items selected)
                        </Typography>
                        <IconButton
                            aria-label="Close modal"
                            onClick={this.onCloseModal}
                        >
                            x
                        </IconButton>
                    </div>

                    <div className={classes.content}>
                        <FormControl component="fieldset" className={classes.modeSelector}>
                            <RadioGroup
                                aria-label="tag mode"
                                name="tagMode"
                                value={mode}
                                onChange={this.onChangeMode}
                                className={classes.radioGroup}
                            >
                                <FormControlLabel 
                                    value={TAG_MODES.ADD} 
                                    control={<Radio color="primary"/>} 
                                    label="Add tags" 
                                />
                                <FormControlLabel 
                                    value={TAG_MODES.REMOVE} 
                                    control={<Radio color="primary"/>} 
                                    label="Remove tags" 
                                />
                            </RadioGroup>
                        </FormControl>

                        <Typography variant="body1" className={classes.description}>
                            {descriptionText}
                        </Typography>
                        
                        <div className={classes.tagsContainer}>
                            <div className={classes.inputContainer}>
                                <Input
                                    placeholder={isAddMode ? "Search/Add Tags..." : "Search..."}
                                    className={classes.input}
                                    endAdornment={
                                        <i className="material-icons">search</i>
                                    }
                                    type='text'
                                    onChange={this.onChangeInput}
                                    value={tagValue}
                                    error={!isTagValid}
                                />
                                {!isTagValid && <div className={classes.error}>Tag is not valid</div>}
                            </div>
                            {addTag}
                            {selectedTags.length > 0 && (
                                <div className={classes.selectedTagsContainer}>
                                    <Typography variant="body2" className={classes.selectedTagsTitle}>
                                        {isAddMode ? 'Selected tags to add:' : 'Selected tags to remove:'}
                                    </Typography>
                                    <div className={classes.selectedTagsList}>
                                        {selectedTags.map((tag, index) => (
                                            <div 
                                                key={index} 
                                                className={isAddMode ? classes.selectedTag : classes.selectedTagRemove}
                                            >
                                                {tag}
                                                <i 
                                                    className="material-icons" 
                                                    onClick={() => this.onTagSelected(tag, 0, true)}
                                                >
                                                    close
                                                </i>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <AssetTagsList
                                tagWithoutSpaces={tagWithoutSpaces}
                                assetTags={selectedTags}
                                tags={tags}
                                onTagSelected={this.onTagSelected}
                                preserveOrder={true}
                            />
                        </div>
                    </div>

                    <div className={classes.footer}>
                        <Button
                            onClick={this.onCloseModal}
                            color="default"
                            className={classes.cancelButton}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={this.onApplyTags}
                            variant="contained"
                            color="primary"
                            disabled={selectedTags.length === 0}
                        >
                            {actionButtonText}
                        </Button>
                    </div>
                </Paper>
                <div
                    className={classes.background}
                    onClick={this.onCloseModal}
                />
            </div>
        );
    }
}

BatchTagModal.propTypes = {
    show: PropTypes.bool.isRequired,
    selectedAssets: PropTypes.array.isRequired,
    executeBatchAction: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    tags: PropTypes.array.isRequired,
    addTag: PropTypes.func.isRequired,
};

const style = ({ palette, spacing }) => ({
    container: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modal: {
        width: 400,
        maxHeight: '80vh',
        zIndex: 1001,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: spacing.unit * 2,
        borderBottom: '1px solid #e0e0e0',
    },
    title: {
        fontWeight: 'bold',
    },
    content: {
        padding: spacing.unit * 2,
        overflowY: 'auto',
        flexGrow: 1,
    },
    description: {
        marginBottom: spacing.unit * 2,
    },
    modeSelector: {
        marginBottom: spacing.unit * 2,
        borderBottom: `1px solid ${palette.grey[300]}`,
        paddingBottom: spacing.unit
    },
    radioGroup: {
        flexDirection: 'row',
    },
    tagsContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    inputContainer: {
        marginBottom: spacing.unit,
    },
    input: {
        width: '100%',
    },
    addNewTag: {
        display: 'flex',
        alignItems: 'center',
        padding: spacing.unit,
        cursor: 'pointer',
        backgroundColor: palette.background.gray3Color,
        marginBottom: spacing.unit,
        borderRadius: 4,
    },
    addNewTagIcon: {
        marginRight: spacing.unit,
    },
    createTagButton: {
        fontWeight: 'bold',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: spacing.unit * 2,
        borderTop: '1px solid #e0e0e0',
    },
    cancelButton: {
        marginRight: spacing.unit,
    },
    background: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
    },
    error: {
        color: 'red',
        fontSize: '0.8rem',
        marginTop: spacing.unit / 2,
    },
    selectedTagsContainer: {
        marginBottom: spacing.unit,
    },
    selectedTagsTitle: {
        fontWeight: 'bold',
        marginBottom: spacing.unit / 2,
    },
    selectedTagsList: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    selectedTag: {
        padding: `${spacing.unit / 2}px ${spacing.unit}px`,
        backgroundColor: palette.primary.light,
        color: palette.primary.contrastText,
        borderRadius: 4,
        marginRight: spacing.unit / 2,
        marginBottom: spacing.unit / 2,
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.85rem',
        '& i': {
            fontSize: '16px',
            marginLeft: spacing.unit / 2,
            cursor: 'pointer',
        }
    },
    selectedTagRemove: {
        padding: `${spacing.unit / 2}px ${spacing.unit}px`,
        backgroundColor: palette.error.light,
        color: palette.error.contrastText,
        borderRadius: 4,
        marginRight: spacing.unit / 2,
        marginBottom: spacing.unit / 2,
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.85rem',
        '& i': {
            fontSize: '16px',
            marginLeft: spacing.unit / 2,
            cursor: 'pointer',
        }
    },
});

const mapStateToProps = state => ({
    tags: tagSelectors.getTagsList(state),
});

const mapDispatchToProps = dispatch => ({
    addTag: (tag) => {
        dispatch({
            type: tagsConstants.ADD_TAG,
            payload: tag,
        });
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(style)(BatchTagModal)); 


// WEBPACK FOOTER //
// ./src/components/modals/BatchTagModal.js